/**
 * Правила Fruit Slice без отрисовки и DOM — для тестов. Перенос 1:1 из
 * iOS (`FruitNinjaConstants`, `FruitObject`, `FruitPlayerState`,
 * `FruitNinjaMultiplayerViewController`, `FruitNinjaGameView.createNewObject*`),
 * чтобы веб-версия игралась так же, как нативная. Числа менять вместе с iOS.
 *
 * Координаты — пиксели поля (x вправо, y вниз), время — секунды игры.
 */

export type FruitMode = 'classic' | 'arcade' | 'zen';

export interface Point {
  x: number;
  y: number;
}

export const RULES = {
  /** Гравитация — доля высоты поля в секунду² (`height × 1.2`). */
  gravityPerHeight: 1.2,
  /** Радиус фрукта — доля высоты (соло); на двоих ×0.75 (`radiusScale`). */
  radiusPerHeight: 0.1,
  duoRadiusScale: 0.75,
  /** Соло горизонтально: горизонтальная скорость 10…20 % большей стороны поля в секунду (`minVelocityOffhand`). */
  soloVelocityXPerBiggest: 0.1,
  soloVelocityXDeltaPerBiggest: 0.1,
  /** Соло «На время» — до 6 фруктов сразу (на двоих — 4 на половину). */
  soloArcadeMaxObjects: 6,
  /**
   * Высота полёта — доля высоты поля (`minPeakHeight` / `maxPeakHeight` нативной Android-игры). Выше 1 фрукт
   * вылетает за верх экрана; при 0,95 центр фрукта в верхней точке остаётся в поле.
   */
  minPeakHeightRatio: 0.5,
  maxPeakHeightRatio: 0.95,
  rotationSpeedBase: 90,
  rotationSpeedRange: 180,
  spawnableWidthRatio: 0.8,
  marginRatio: 0.1,
  horizontalDistanceMin: 0.5,
  horizontalDistanceRange: 0.3,
  fallbackVelocityX: 200,
  sliceSeparationStartFactor: 0.5,
  sliceSeparationVelocity: 200,
  sliceRotationFactor: 0.25,
  sliceVelocityDivider: 1.5,
  sliceVelocityYDivider: 2,
  sliceMinFallSpeed: 100,
  comboWindow: 0.5,
  comboDisplay: 1,
  comboMinHits: 3,
  maxLives: 3,
  maxInitialSpawnDelay: 1,
  maxFinalSpawnDelay: 0.3,
  spawnDelayDecayPerSec: 0.01,
  difficultyIncreaseInterval: 20,
  arcadeDuration: 60,
  bombPenaltyArcade: -5,
  /** Хитбокс руки — доля меньшей стороны поля. */
  handHitboxPercent: 0.05,
  /** Взмах — рука быстрее этого, меньших сторон поля в секунду (по меткам времени кадров поз). */
  minSwipeSpeedPerSmallest: 1.5,
  /** Скачок точки больше этой доли большей стороны за один кадр — сбой трекинга, а не взмах. */
  maxSwipeJumpPerBiggest: 0.35,
  /** Прошлая точка старее этого (мс) не считается: рука пропадала. */
  swipeMaxSampleGapMs: 250,
  /** Нижние 20 % поля: взлетающий фрукт там ещё не режется. */
  bottomExclusionRatio: 0.8,
} as const;

export const MODE_TUNING: Record<FruitMode, {
  bombChance: number;
  hasLives: boolean;
  initialMaxObjects: number;
  maxObjects: number;
  timeLimit: number | null;
}> = {
  classic: {
    bombChance: 0.1, hasLives: true, initialMaxObjects: 1, maxObjects: 3, timeLimit: null,
  },
  arcade: {
    bombChance: 0.1, hasLives: false, initialMaxObjects: 3, maxObjects: 4, timeLimit: RULES.arcadeDuration,
  },
  zen: {
    bombChance: 0, hasLives: false, initialMaxObjects: 1, maxObjects: 3, timeLimit: null,
  },
};

export const FRUIT_KINDS = ['APPLE', 'KIWI', 'PEACH', 'STRAWBERRY', 'WATERMELON'] as const;
export type FruitKind = typeof FRUIT_KINDS[number];

export interface Fruit {
  readonly id: number;
  readonly kind: FruitKind | 'BOMB';
  readonly player: 0 | 1;
  position: Point;
  velocity: Point;
  readonly radius: number;
  rotation: number;
  readonly rotationSpeed: number;
  sliced: boolean;
  sliceAngle: number;
  separation: number;
  half1: Point;
  half2: Point;
  rotation1: number;
  rotation2: number;
}

export interface Field {
  readonly width: number;
  readonly height: number;
}

export const gravity = (field: Field) => field.height * RULES.gravityPerHeight;
/** Сколько игроков: 1 — всё поле одного, 2 — по половине. */
export type Players = 1 | 2;

export const fruitRadius = (field: Field, players: Players = 2) => field.height * RULES.radiusPerHeight
  * (players === 2 ? RULES.duoRadiusScale : 1);

/**
 * Скорость взлёта между минимальной и максимальной высотой. Обе скорости отрицательные (вверх), поэтому
 * разность тоже отрицательная — её нельзя зажимать нулём (так в нативной iOS, и высота всегда минимальная).
 */
function verticalVelocity(field: Field, random: () => number): number {
  const g = gravity(field);
  const minVelocityY = -Math.sqrt(2 * g * field.height * RULES.minPeakHeightRatio);
  const maxVelocityY = -Math.sqrt(2 * g * field.height * RULES.maxPeakHeightRatio);
  return minVelocityY + random() * (maxVelocityY - minVelocityY);
}

function pickKind(mode: FruitMode, random: () => number): Fruit['kind'] {
  const isBomb = random() < MODE_TUNING[mode].bombChance;
  return isBomb ? 'BOMB' : FRUIT_KINDS[Math.floor(random() * FRUIT_KINDS.length) % FRUIT_KINDS.length];
}

function makeFruit(
  base: { id: number; kind: Fruit['kind']; player: 0 | 1; x: number; field: Field; vx: number; vy: number; xSide: number },
  players: Players,
  random: () => number,
): Fruit {
  const position = { x: base.x, y: base.field.height };
  return {
    id: base.id,
    kind: base.kind,
    player: base.player,
    position,
    velocity: { x: base.vx, y: base.vy },
    radius: fruitRadius(base.field, players),
    rotation: 0,
    rotationSpeed: base.xSide * (random() * RULES.rotationSpeedRange + RULES.rotationSpeedBase),
    sliced: false,
    sliceAngle: 0,
    separation: 0,
    half1: { ...position },
    half2: { ...position },
    rotation1: 0,
    rotation2: 0,
  };
}

/** Соло, горизонтально: фрукт с любого места снизу летит к середине — как `createNewObject(useHorizontal:)`. */
export function spawnFruitSolo(field: Field, mode: FruitMode, id: number, random: () => number): Fruit {
  const x = random() * field.width;
  const xSide = x > field.width / 2 ? -1 : 1;
  const biggest = Math.max(field.width, field.height);
  const vx = xSide * biggest * (RULES.soloVelocityXPerBiggest + random() * RULES.soloVelocityXDeltaPerBiggest);
  const vy = verticalVelocity(field, random);
  return makeFruit({ id, kind: pickKind(mode, random), player: 0, x, field, vx, vy, xSide }, 1, random);
}

/** Новый фрукт на своей половине поля: взлетает снизу и падает обратно в свою половину. */
export function spawnFruit(field: Field, player: 0 | 1, mode: FruitMode, id: number, random: () => number): Fruit {
  const { width, height } = field;
  const g = gravity(field);
  const half = width / 2;
  const quarter = width / 4;
  const spawnable = half * RULES.spawnableWidthRatio;
  const margin = half * RULES.marginRatio;
  const startX = (player === 0 ? margin : half + margin) + random() * spawnable;
  const towardsCenter = startX < quarter || startX > half + quarter;
  const sign = player === 0 ? 1 : -1;
  const xSide = towardsCenter ? sign : -sign;

  const velocityY = verticalVelocity(field, random);
  const airTime = (-velocityY / g) * 2;
  const maxDistance = Math.max(0, player === 0 ? half - startX : startX - half);
  const distance = maxDistance * (RULES.horizontalDistanceMin + random() * RULES.horizontalDistanceRange);
  const velocityX = airTime > 0 ? xSide * (distance / airTime) : xSide * RULES.fallbackVelocityX;

  const isBomb = random() < MODE_TUNING[mode].bombChance;
  const kind = isBomb ? 'BOMB' : FRUIT_KINDS[Math.floor(random() * FRUIT_KINDS.length) % FRUIT_KINDS.length];
  const position = { x: startX, y: height };
  return {
    id,
    kind,
    player,
    position,
    velocity: { x: velocityX, y: velocityY },
    radius: fruitRadius(field),
    rotation: 0,
    rotationSpeed: xSide * (random() * RULES.rotationSpeedRange + RULES.rotationSpeedBase),
    sliced: false,
    sliceAngle: 0,
    separation: 0,
    half1: { ...position },
    half2: { ...position },
    rotation1: 0,
    rotation2: 0,
  };
}

function placeHalves(fruit: Fruit): void {
  const perpendicular = fruit.sliceAngle + Math.PI / 2;
  const dx = Math.cos(perpendicular) * fruit.separation;
  const dy = Math.sin(perpendicular) * fruit.separation;
  fruit.half1 = { x: fruit.position.x + dx, y: fruit.position.y + dy };
  fruit.half2 = { x: fruit.position.x - dx, y: fruit.position.y - dy };
}

/** Шаг физики: полёт, вращение, расхождение половинок. Порядок — как в iOS. */
export function stepFruit(fruit: Fruit, field: Field, dt: number): void {
  if (fruit.sliced && fruit.kind !== 'BOMB') {
    if (fruit.separation < fruit.radius) {
      fruit.separation = Math.min(fruit.radius, fruit.separation + RULES.sliceSeparationVelocity * dt);
      placeHalves(fruit);
    }
    fruit.half1.x += fruit.velocity.x * dt;
    fruit.half1.y += fruit.velocity.y * dt;
    fruit.half2.x += fruit.velocity.x * dt;
    fruit.half2.y += fruit.velocity.y * dt;
    fruit.rotation1 += fruit.rotationSpeed * RULES.sliceRotationFactor * dt;
    fruit.rotation2 -= fruit.rotationSpeed * RULES.sliceRotationFactor * dt;
  }
  fruit.position.x += fruit.velocity.x * dt;
  fruit.position.y += fruit.velocity.y * dt;
  fruit.velocity.y += gravity(field) * dt;
  fruit.rotation += fruit.rotationSpeed * dt;
}

export function sliceFruit(fruit: Fruit, angle: number): void {
  fruit.sliced = true;
  fruit.sliceAngle = angle;
  if (fruit.kind === 'BOMB') return;
  fruit.separation = fruit.radius * RULES.sliceSeparationStartFactor;
  placeHalves(fruit);
  const degrees = (angle * 180) / Math.PI;
  fruit.rotation1 = degrees;
  fruit.rotation2 = degrees + 180;
  fruit.velocity.x /= RULES.sliceVelocityDivider;
  fruit.velocity.y = Math.max(fruit.velocity.y / RULES.sliceVelocityYDivider, RULES.sliceMinFallSpeed);
}

export function isOutOfView(fruit: Fruit, field: Field): boolean {
  const out = (p: Point) => p.x + fruit.radius < 0 || p.x - fruit.radius > field.width
    || p.y - fruit.radius > field.height;
  if (fruit.sliced && fruit.kind !== 'BOMB') return out(fruit.half1) && out(fruit.half2);
  return out(fruit.position);
}

/**
 * Где на отрезке ближе всего к центру круга (0…1), если отрезок проходит в пределах `radius`; иначе -1 —
 * `segmentCircleHit` нативной Android-игры. Порядок по `t` — в каком порядке лезвие дошло до фруктов.
 */
export function segmentCircleHitAt(start: Point, end: Point, center: Point, radius: number): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSq = dx * dx + dy * dy;
  const along = ((center.x - start.x) * dx + (center.y - start.y) * dy) / lengthSq;
  const t = lengthSq <= 1e-9 ? 0 : Math.min(1, Math.max(0, along));
  const nearestX = start.x + t * dx - center.x;
  const nearestY = start.y + t * dy - center.y;
  return nearestX * nearestX + nearestY * nearestY <= radius * radius ? t : -1;
}

/**
 * Помнит прошлую точку руки и решает, взмах ли движение к новой — `SwipeTracker` нативной Android-игры. Прошлая
 * точка заменяется на каждом кадре: медленный дрейф не складывается во взмах, а взмах судят по скорости
 * (пикс/с по меткам времени кадров поз).
 */
// eslint-disable-next-line max-classes-per-file
export class SwipeTracker {
  private x = 0;

  private y = 0;

  private time = 0;

  private known = false;

  /** Начало отрезка последнего взмаха, когда `update` вернул `true`. */
  fromX = 0;

  fromY = 0;

  reset(): void {
    this.known = false;
  }

  update(
    x: number,
    y: number,
    timeMs: number,
    visible: boolean,
    minSpeed: number,
    maxJump: number,
    maxGapMs: number,
  ): boolean {
    if (!visible) {
      this.known = false;
      return false;
    }
    const hadPrevious = this.known;
    const previousX = this.x;
    const previousY = this.y;
    const previousTime = this.time;
    this.x = x;
    this.y = y;
    this.time = timeMs;
    this.known = true;
    if (!hadPrevious) return false;
    const dt = timeMs - previousTime;
    if (dt <= 0 || dt > maxGapMs) return false;
    const distance = Math.hypot(x - previousX, y - previousY);
    if (distance > maxJump) return false;
    if ((distance * 1000) / dt < minSpeed) return false;
    this.fromX = previousX;
    this.fromY = previousY;
    return true;
  }
}

/**
 * Убирает из `fruits` на месте те, для которых `leaves` вернула `true` (она же двигает фрукт), и складывает их в
 * `gone`. Последствия ухода (потеря жизни, конец партии) обрабатываются уже после: конец партии заменяет и
 * очищает `fruits`, и это не должно происходить посреди прохода по массиву.
 */
export function sweepFruits(fruits: Fruit[], leaves: (fruit: Fruit) => boolean, gone: Fruit[]): void {
  gone.length = 0;
  let kept = 0;
  for (let i = 0; i < fruits.length; i += 1) {
    const fruit = fruits[i];
    if (leaves(fruit)) {
      gone.push(fruit);
    } else {
      fruits[kept] = fruit;
      kept += 1;
    }
  }
  fruits.length = kept;
}

/** Максимальная задержка появления: падает со временем от 1 до 0,3 с. */
export function maxSpawnDelay(gameTime: number): number {
  const delay = RULES.maxInitialSpawnDelay - gameTime * RULES.spawnDelayDecayPerSec;
  return Math.min(RULES.maxInitialSpawnDelay, Math.max(RULES.maxFinalSpawnDelay, delay));
}

/** Сколько фруктов сразу у игрока: +1 каждые 20 с до потолка режима. */
export function maxObjects(mode: FruitMode, gameTime: number, players: Players = 2): number {
  const { initialMaxObjects } = MODE_TUNING[mode];
  const cap = players === 1 && mode === 'arcade' ? RULES.soloArcadeMaxObjects : MODE_TUNING[mode].maxObjects;
  return Math.min(cap, initialMaxObjects + Math.floor(gameTime / RULES.difficultyIncreaseInterval));
}

/** Счёт, серия, жизни и комбо одного игрока — как `FruitPlayerState`. */
export class PlayerState {
  score = 0;

  lives: number = RULES.maxLives;

  streak = 0;

  eliminated = false;

  comboCount = 0;

  lastSliceAt = -Infinity;

  /** Показанное комбо: сколько и где; очки за него приходят после показа. */
  combo: { count: number; at: Point; shownAt: number } | null = null;

  reset(mode: FruitMode): void {
    this.score = 0;
    this.lives = MODE_TUNING[mode].hasLives ? RULES.maxLives : 0;
    this.streak = 0;
    this.eliminated = false;
    this.comboCount = 0;
    this.lastSliceAt = -Infinity;
    this.combo = null;
  }

  addScore(points: number): void {
    this.score += points;
    this.streak = points > 0 ? this.streak + 1 : 0;
  }

  /** Разрез в момент `now`: комбо из 3+ фруктов за 0,5 с между разрезами; бомба сбрасывает. */
  registerSlice(fruit: Fruit, now: number): void {
    const inWindow = now - this.lastSliceAt < RULES.comboWindow;
    if (fruit.kind === 'BOMB') this.comboCount = 0;
    else this.comboCount = inWindow ? this.comboCount + 1 : 1;
    this.lastSliceAt = now;
    if (this.comboCount >= RULES.comboMinHits) {
      const at = { x: fruit.position.x - fruit.radius, y: fruit.position.y };
      this.combo = { count: this.comboCount, at, shownAt: now };
    }
  }

  /** Комбо показано секунду — очки за него начисляются. `true`, если счёт изменился. */
  commitCombo(now: number): boolean {
    if (!this.combo || now - this.combo.shownAt < RULES.comboDisplay) return false;
    this.score += this.combo.count;
    this.combo = null;
    this.comboCount = 0;
    return true;
  }
}
